import {useCallback} from 'react'
import {
  clampPanelWidth,
  resolvePairedDrag,
  splitFullWidth,
  type PanelPair,
} from './panelWidth'

/** Body class while a lateral panel is drag-resized — CSS can also target iframes if needed. */
export const SIDE_PANEL_RESIZING_CLASS = 'ob-side-panel-resizing'

/**
 * Full-viewport shield so results-viewer iframes cannot steal mouse events mid-drag.
 * Without this, a fast drag that crosses into an iframe loses mousemove/mouseup on the parent
 * document and the resize "sticks" until the next click.
 */
function mountResizeShield(): HTMLDivElement {
  const shield = document.createElement('div')
  shield.setAttribute('data-ob-side-panel-resize-shield', '')
  Object.assign(shield.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    cursor: 'col-resize',
  })
  document.body.appendChild(shield)
  return shield
}

function flexRowRect(): DOMRect | undefined {
  return document.querySelector('.d-flex.flex-column.flex-lg-row')?.getBoundingClientRect()
}

export interface SidePanelResizeOptions {
  /** When true, the centre is already hidden — clamp with min centre 0. */
  getCenterCollapsed?: () => boolean
  /**
   * Fired when the centre and the opposite panel are both at their minimum and the drag keeps
   * going. Receives the Files/Chat split that fills the row so the caller can hide `#wrapper`.
   */
  onCollapseCenter?: (split: PanelPair) => void
  /** Fired while both panels are open, once the drag starts squeezing the opposite panel. */
  onPairResize?: (pair: PanelPair) => void
}

/**
 * Drag-to-resize for a lateral panel, parameterised by which side it is docked to.
 *
 * Lifted from the AI chat's hand-rolled handler so the Files panel does not grow a second copy of
 * it. The only real difference is the anchor: a right-docked panel grows as the pointer moves left
 * (`right - clientX`), a left-docked one as it moves right (`clientX - left`).
 *
 * `getOtherPanelWidth` is a getter rather than a value so the handler identity stays stable while
 * the other panel is being resized mid-drag.
 */
export function useSidePanelResize(
  side: 'left' | 'right',
  onWidthChange: ((width: number) => void) | undefined,
  getOtherPanelWidth: () => number,
  opts: SidePanelResizeOptions = {}
) {
  const {getCenterCollapsed, onCollapseCenter, onPairResize} = opts

  return useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.classList.add(SIDE_PANEL_RESIZING_CLASS)

      const shield = mountResizeShield()
      // Read once: the opposite panel's width at drag start is what it grows back to if the user
      // drags back within the same gesture. The getters are captured at drag start too, so once the
      // gesture snaps the centre closed the rest of it keeps splitting the full row.
      const otherStartWidth = getOtherPanelWidth()
      const centerCollapsed = getCenterCollapsed?.() === true
      let collapsedDuringDrag = false

      const onMove = (move: MouseEvent) => {
        // The flex row that holds [files panel | wrapper | chat panel] in MasterLayout.
        const rect = flexRowRect()
        if (!rect) return

        const raw = side === 'left' ? move.clientX - rect.left : rect.right - move.clientX
        const viewportWidth = rect.width

        if (collapsedDuringDrag && onCollapseCenter) {
          onCollapseCenter(
            splitFullWidth(side === 'left' ? raw : viewportWidth - raw, viewportWidth)
          )
          return
        }

        if (!centerCollapsed && otherStartWidth > 0 && onCollapseCenter && onPairResize) {
          const {collapse, ...pair} = resolvePairedDrag(side, raw, otherStartWidth, viewportWidth)
          if (collapse) {
            collapsedDuringDrag = true
            onCollapseCenter(pair)
          } else {
            onPairResize(pair)
          }
          return
        }

        onWidthChange?.(
          clampPanelWidth(raw, {
            otherPanelWidth: otherStartWidth,
            viewportWidth,
            centerCollapsed,
          })
        )
      }

      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.body.classList.remove(SIDE_PANEL_RESIZING_CLASS)
        shield.remove()
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [side, onWidthChange, getOtherPanelWidth, getCenterCollapsed, onCollapseCenter, onPairResize]
  )
}

/**
 * Single shared divider between Files and Chat when the centre column is collapsed.
 * Dragging moves the split so both panels always fill the row.
 */
export function useSharedPanelSplit(
  onSplitChange: (split: {filesWidth: number; chatWidth: number}) => void
) {
  return useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.classList.add(SIDE_PANEL_RESIZING_CLASS)

      const shield = mountResizeShield()

      const onMove = (move: MouseEvent) => {
        const rect = flexRowRect()
        if (!rect) return
        onSplitChange(splitFullWidth(move.clientX - rect.left, rect.width))
      }

      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.body.classList.remove(SIDE_PANEL_RESIZING_CLASS)
        shield.remove()
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [onSplitChange]
  )
}
